import React, {useState, useEffect, useRef, useMemo} from 'react'
import {Form} from '@bedrock/components'
import {useControllableValue, useDebounceEffect, useMemoizedFn, useMount, useUpdateLayoutEffect} from "ahooks";
import {isEqual, get, set} from 'lodash'
import {FormItemKeyType} from "#components/SForm/SFormItem/index";
import {formItemKeyToList} from "#components/SForm/SFormItem/utils";

type FormItemValueTransformProps = {
    // 更具keyMap，生成隐藏的formItem,用于给form赋值
    keyMap?: Record<string, FormItemKeyType>;
} & React.ComponentProps<typeof Form.Item>

function isUndefined(value: any) {
    return typeof value === 'undefined';
}

type ChildItemProps = {
    value?: any;
    onChange?: (value: any) => void;

    valueKey: string;
    parentValue: any;
    parentOnChange: (value: any) => void;
}
// 虚拟子组件,用于传递value和onChange，同步给FormItem
function VirtualChildItem({parentValue, valueKey,parentOnChange, ...props}: ChildItemProps) {
    const [value, setValue] = useControllableValue(props);
    const [localValue, setLocalValue] = useState(value);

    const parentShowValue = get(parentValue || {}, valueKey, null);

    const changeValue = useMemoizedFn(() => {
        if (isUndefined(parentShowValue) && isUndefined(value)) return;
        if (!isEqual(value, localValue)) {
            // value外部变更，赋值并改变外部parent
            setLocalValue(value);
            parentOnChange({
                [valueKey]: value
            });
        } else if (!isEqual(parentShowValue, value)) {
            // parent变更，赋值并改变local
            setValue(parentShowValue);
            setLocalValue(parentShowValue);
        }
    })


    useMount(() => {
        // 初始化时,如果内部有值，则以内部为准，向parent同步
        if (!isEqual(parentShowValue, value) && !isUndefined(value)) {
            parentOnChange({
                [valueKey]: value
            });
        }
    })

    useUpdateLayoutEffect(() => {
        changeValue();
    }, [value, localValue, parentValue?.[valueKey]]);
    return <></>;
}

// 包裹子组件，用于传递value和onChange
type WrapperChildrenProps = {
    wrapperValue: any;
    wrapperOnChange: (value: any) => void;
    children: React.ReactNode;
}

function WrapperChildren({children, wrapperValue, wrapperOnChange, ...props}: WrapperChildrenProps) {
    // 用于重写value和onChange，
    const [value, setValue] = useControllableValue(props);
    const [localValue, setLocalValue] = useState(value);

    const changeValue = useMemoizedFn(() => {
        if (!wrapperValue && isUndefined(value)) return;
        // 判断如果value不是对象或数组则报错，不进行赋值
        if (typeof value !== 'object' && wrapperValue) {
            setValue(wrapperValue);
            setLocalValue(wrapperValue);
            return;
        }

        if (!isEqual(value, localValue)) {
            // value外部变更，赋值并改变外部parent
            setLocalValue(value);
            wrapperOnChange?.(value)
        } else if (!isEqual(wrapperValue, value)) {
            // parent变更，赋值并改变local
            setValue(wrapperValue);
            setLocalValue(wrapperValue);
        }
    })

    useDebounceEffect(() => {
        changeValue();
    }, [value, localValue, wrapperValue], {
        wait: 40,
        leading: false,
        trailing: true
    });

    let childrenArray = React.Children.toArray(children);
    if (childrenArray.length !== 1) {
        return children;
    }
    if (!React.isValidElement(children)) {
        return children;
    }
    return React.cloneElement(children as any, {
        value,
        onChange: setValue
    });
}

export default function FormItemValueTransform({
    keyMap,
    ...props
                                  }: FormItemValueTransformProps) {

    const [wrapperValue, setWrapperValue] = useState({});

    const updateWrapperValue = useMemoizedFn((value: any) => {
        setWrapperValue(oldValue => {
            return {
                ...oldValue,
                ...value
            }
        });
    });

    const formItems = useMemo(() => {
        if (!keyMap || !wrapperValue) {
            return null;
        }
        return Object.keys(keyMap).map(key => {
            const formItemKey = keyMap[key];
            return <Form.Item
                key={key}
                name={formItemKeyToList(formItemKey)}
                style={{display: 'none'}}>
                <VirtualChildItem
                    valueKey={key}
                    parentValue={wrapperValue}
                    parentOnChange={updateWrapperValue}
                />
            </Form.Item>
        })
    }, [keyMap, wrapperValue]);

    return <>
        <Form.Item {...props}>
            <WrapperChildren
                wrapperValue={wrapperValue}
                wrapperOnChange={updateWrapperValue}
            >
                {props.children}
            </WrapperChildren>
        </Form.Item>
        {formItems}
    </>
}
